import React from "react";
import { Button, IconButton, InputAdornment } from "@mui/material";
import { ArrowBack, Search } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import moment from "moment";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import PreloaderTable from "../../lib/components/PreloaderTable";
import DataTable from "../../lib/components/DataTable";
import BTNLoader from "../../lib/components/BTNLoader";
import ConfirmationDialog from "../../lib/components/ConfirmationDialog";
import organizationservice from "../../service/organizationservice";
import errorHandler from "../../utils/errorHandler";
import scrollHandler from "../../utils/scrollHandler";
import FirsBusinessLimitDialog from "./components/FirsBusinessLimitDialog";

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);

const OrganizationFirsBusinesses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const search = searchParams.get("search") || "";
  const [searchText, setSearchText] = React.useState(search);
  const [isLimitOpen, setIsLimitOpen] = React.useState(false);
  const [removing, setRemoving] = React.useState(null);

  const organizationQuery = useQuery({
    retry: false,
    queryKey: ["organizations-details", id],
    queryFn: () => organizationservice.getOne({ id }),
  });

  const businessesQuery = useQuery({
    retry: false,
    queryKey: ["firs-businesses", id, page, limit, search],
    queryFn: () => organizationservice.getFirsBusinesses(id, { page, limit, search }),
  });

  React.useEffect(() => {
    if (searchText === search) return;
    const timeoutId = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      params.set("search", searchText);
      params.set("page", "1");
      navigate({ pathname: window.location.pathname, search: params.toString() }, { replace: true });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchText, search, navigate]);

  const removeMutation = useMutation({
    mutationFn: (businessId) => organizationservice.removeFirsBusiness(id, businessId),
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      toast.success("Business removed successfully.");
      setRemoving(null);
      businessesQuery.refetch();
    },
  });

  const organization = organizationQuery.data?.data?.data;
  const result = businessesQuery.data?.data?.data;
  const businesses = result?.businesses;
  const maxBusinesses = result?.maxBusinesses;
  const businessCount = result?.businessCount ?? 0;

  const columns = [
    { key: "businessName", header: "Business Name", render: (value) => value || "-" },
    { key: "businessId", header: "Business ID" },
    { key: "tin", header: "TIN", render: (value) => value || "-" },
    {
      key: "firstSignedAt",
      header: "First Signed",
      render: (value) => (value ? moment(value).format("DD MMM YYYY, h:mm A") : "-"),
    },
  ];

  const rowActions = [{ label: "Remove", color: "error", onClick: (row) => setRemoving(row) }];

  return (
    <AppLayout>
      <Header title="NRS Businesses" description="Businesses this organization has signed invoices with" />

      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <IconButton className="bg-secondary-subtle mb-3" onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <div className="d-flex justify-content-between mb-4">
          <div>
            <div className="fs-4 fw-medium">{organization?.name}</div>
            <div className="text-secondary fs-sm">Businesses this organization has signed invoices with on NRS</div>
          </div>
          <BTNLoader isLoading={organizationQuery.isPending} />
        </div>

        <div className="bg-white border rounded p-3 mb-4 d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <div>
            <div className="text-secondary fs-sm">Businesses in use</div>
            <div className="fs-4 fw-medium">
              {businessCount} of {maxBusinesses === null ? "Unlimited" : maxBusinesses ?? "-"}
            </div>
          </div>
          <Button variant="contained" color="secondary" disabled={businessesQuery.isPending} onClick={() => setIsLimitOpen(true)}>
            Change limit
          </Button>
        </div>

        <div className="row mb-3">
          <div className="col-12 col-md-6 col-lg-4">
            <AppTextField
              placeholder="Search by name, business ID or TIN..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>

        <PreloaderTable isLoading={businessesQuery.isPending} />
        <DataTable
          data={businesses?.docs ?? []}
          columns={columns}
          rowActions={rowActions}
          getRowId={(row) => row._id ?? row.businessId}
          isLoading={businessesQuery.isPending}
          page={Number(page)}
          pageSize={Number(limit)}
          totalPages={businesses?.totalPages}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) => handleChange(value, "limit")}
        />
      </div>

      <FirsBusinessLimitDialog
        isOpen={isLimitOpen}
        onClose={() => setIsLimitOpen(false)}
        organizationId={id}
        maxBusinesses={maxBusinesses}
        businessCount={businessCount}
        onSaved={() => businessesQuery.refetch()}
      />

      <ConfirmationDialog
        isOpen={!!removing}
        onClose={() => setRemoving(null)}
        title="Remove business"
        body={`Remove <b>${escapeHtml(removing?.businessId)}</b>? This frees one slot. The business is counted again if the organization signs with it later.`}
        type="danger"
        actionTitle="Remove"
        isLoading={removeMutation.isPending}
        onSubmit={() => removeMutation.mutate(removing.businessId)}
      />
    </AppLayout>
  );

  function handleChange(value, name) {
    searchParams.set(name, value);
    if (name === "limit") searchParams.set("page", "1");
    navigate({ pathname: window.location.pathname, search: searchParams.toString() }, { replace: true });
    if (name === "page") scrollHandler();
  }
};

export default OrganizationFirsBusinesses;
